<template>
  <div class="flex flex-col gap-6">
    <div v-if="screens.length === 0" class="flex flex-col gap-4">
      <p class="text-body-large text-on-surface-variant">{{ $t('portal.feedback.empty') }}</p>
      <div class="flex justify-end">
        <BaseTextButton size="M" type="button" @click="emit('back')">
          {{ $t('portal.feedback.back') }}
        </BaseTextButton>
      </div>
    </div>

    <div v-else-if="current" class="flex h-screen flex-col">
      <div class="flex w-full grow items-center justify-center overflow-hidden">
        <StepTransition :direction="direction">
          <div :key="screenItemId(current)" class="flex w-full flex-col gap-6">
            <h2 class="text-headline-large text-center text-balance">{{ current.text }}</h2>

            <BaseButtonGroup v-if="current.kind === 'indikator'" size="M">
              <BaseTextButton
                v-for="item in questionnaire.scale.items"
                size="M"
                :key="item.value"
                color="tonalPrimary"
                toggle
                :selected="selectedValue === item.value"
                class="h-24! grow basis-0"
                @click="setIndikatorAnswer(current.id, item.value)"
              >
                <span class="inline-flex flex-col items-center gap-2">
                  <span class="inline-flex flex-row items-center gap-0.5">
                    <span
                      v-for="dot in questionnaire.scale.items"
                      class="h-2.5 w-2.5 rounded-full"
                      :class="[
                        dot.value <= item.value && selectedValue !== item.value && 'bg-primary',
                        dot.value > item.value &&
                          selectedValue !== item.value &&
                          'border-primary border-2',
                        dot.value <= item.value && selectedValue === item.value && 'bg-on-primary',
                        dot.value > item.value &&
                          selectedValue === item.value &&
                          'border-on-primary border-2',
                      ]"
                    ></span>
                  </span>
                  <span class="text-title-medium">{{ item.label }}</span>
                </span></BaseTextButton
              >
            </BaseButtonGroup>

            <div v-else class="flex flex-col gap-2">
              <p class="text-body-medium text-on-surface-variant">
                {{ $t('portal.feedback.reflexionHint') }}
              </p>
              <BaseInputField
                v-model="reflexionModel"
                type="multiText"
                :label="$t('portal.feedback.reflexionLabel')"
                :max-length="2000"
                :disabled="submitting"
                hide-decorators
              />
            </div>

            <p v-if="errorMessage" class="text-body-medium text-error">{{ errorMessage }}</p>
          </div>
        </StepTransition>
      </div>

      <StepNavigationBar
        :items="navigationItems"
        :current-id="screenItemId(current)"
        :back-disabled="submitting"
        :next-disabled="!canAdvance || submitting"
        :back-label="$t('portal.feedback.back')"
        :next-label="isLast ? $t('portal.feedback.submit') : $t('portal.feedback.next')"
        @back="goBack"
        @next="goForward"
        @select="goToScreen"
      />
    </div>
  </div>
</template>

<script lang="ts" setup>
  import { computed, ref } from 'vue'
  import BaseInputField from '@/core/components/base/form-fields/BaseInputField.vue'
  import BaseTextButton from '@/core/components/base/button/text-button/BaseTextButton.vue'
  import StepNavigationBar from '@/core/components/step-flow/StepNavigationBar.vue'
  import StepTransition from '@/core/components/step-flow/StepTransition.vue'
  import type { PortalSelfAssessment } from '../models/portal-session'
  import { usePortalDraft } from '../composables/use-portal-draft'
  import BaseButtonGroup from '@/core/components/base/button/BaseButtonGroup.vue'

  const props = defineProps<{
    questionnaire: PortalSelfAssessment
    surveyId: string
    errorMessage?: string
    submitting: boolean
  }>()

  const emit = defineEmits<{
    back: []
    submit: []
  }>()

  const { draft, setIndikatorAnswer, setReflexionText, setScreenIndex } = usePortalDraft(
    () => props.surveyId,
  )

  type Screen = { kind: 'indikator' | 'reflexion'; id: string; text: string }

  const screens = computed<Screen[]>(() => [
    ...props.questionnaire.indikatoren.map((item) => ({
      kind: 'indikator' as const,
      id: item.id,
      text: item.text,
    })),
    ...props.questionnaire.reflexionQuestions.map((item) => ({
      kind: 'reflexion' as const,
      id: item.id,
      text: item.text,
    })),
  ])

  const direction = ref<'next' | 'previous'>('next')

  const screenItemId = (screen: Screen) => `${screen.kind}:${screen.id}`

  const index = computed(() => {
    if (screens.value.length === 0) {
      return 0
    }
    return Math.min(Math.max(draft.value.screenIndex, 0), screens.value.length - 1)
  })

  const navigationItems = computed(() =>
    screens.value.map((screen) => ({
      id: screenItemId(screen),
      finished:
        screen.kind === 'indikator'
          ? draft.value.indikatorAnswers[screen.id] != null
          : (draft.value.reflexionTexts[screen.id] ?? '').trim().length > 0,
    })),
  )

  const current = computed(() => screens.value[index.value])
  const isLast = computed(
    () => screens.value.length > 0 && index.value === screens.value.length - 1,
  )

  const selectedValue = computed(() => {
    if (current.value?.kind !== 'indikator') {
      return undefined
    }
    return draft.value.indikatorAnswers[current.value.id]
  })

  const canAdvance = computed(() => {
    if (!current.value) {
      return false
    }
    if (current.value.kind === 'reflexion') {
      return true
    }
    return selectedValue.value != null
  })

  const reflexionModel = computed<string | undefined>({
    get() {
      if (current.value?.kind !== 'reflexion') {
        return undefined
      }
      const text = draft.value.reflexionTexts[current.value.id]
      return text ? text : undefined
    },
    set(value) {
      if (current.value?.kind !== 'reflexion') {
        return
      }
      setReflexionText(current.value.id, value ?? '')
    },
  })

  const goBack = () => {
    if (props.submitting) {
      return
    }
    if (index.value === 0) {
      emit('back')
      return
    }
    direction.value = 'previous'
    setScreenIndex(index.value - 1)
  }

  const goForward = () => {
    if (!canAdvance.value || props.submitting) {
      return
    }
    if (isLast.value) {
      emit('submit')
      return
    }
    direction.value = 'next'
    setScreenIndex(index.value + 1)
  }

  const goToScreen = (itemId: string) => {
    if (props.submitting) {
      return
    }
    const nextIndex = screens.value.findIndex((screen) => screenItemId(screen) === itemId)
    if (nextIndex < 0 || nextIndex === index.value) {
      return
    }
    direction.value = nextIndex > index.value ? 'next' : 'previous'
    setScreenIndex(nextIndex)
  }
</script>
